import { useState, useEffect } from "react";
import image from "../../Images/EventDrivenNotification.png"
import "../ContainerWebApp/ContainerWebApp.css"
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneDark} from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";

const EventDrivenNotification = () => {
    const [activeSection, setActiveSection] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll("[data-section]");
            let current = "";

            sections.forEach((section) => {
                const sectionTop = section.offsetTop;
                const sectionHeight = section.clientHeight;
                if (window.pageYOffset >= sectionTop - 100) {
                    current = section.getAttribute("data-section");
                }
            });

            setActiveSection(current);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToSection = (sectionId) => {
        const element = document.querySelector(`[data-section="${sectionId}"]`);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div className="documentation-layout">
            <aside className="sidebar">
                <nav className="sidebar-nav">
                    <button className="back-button" onClick={() => navigate('/')}>
                        ← Back
                    </button>
                    <h3 className="sidebar-title">Contents</h3>
                    <ul className="sidebar-list">

                        <li>
                            <a 
                                className={activeSection === "architecture" ? "active" : ""}
                                onClick={() => scrollToSection("architecture")}
                            >
                                Architecture
                            </a>
                        </li>
                            <a 
                                className={activeSection === "details" ? "active" : ""}
                                onClick={() => scrollToSection("details")}
                            >
                                Details
                            </a>
                        <li>
                            <a 
                                className={activeSection === "instantiation-code" ? "active" : ""}
                                onClick={() => scrollToSection("instantiation-code")}
                            >
                                Instantiation
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "stack-code" ? "active" : ""}
                                onClick={() => scrollToSection("stack-code")}
                            >
                                Stack Code
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "implementation-details" ? "active" : ""}
                                onClick={() => scrollToSection("implementation-details")}
                            >
                                Implementation Details
                            </a>
                        </li>
                    </ul>
                </nav>
            </aside>

            <main className="documentation-page">
                <div className="title-container" data-section="title">
                    <ReactMarkdown>
                        {`# Event Driven Notification Stack`}
                    </ReactMarkdown>
                </div>

                <div className="image-container" data-section="architecture">
                    <img className="image" src={image} alt="Architecture Diagram" />
                </div>

                <div className="markdown-left" data-section="details">
                    <ReactMarkdown>
                        {`
## Architecture
- **EventBridge**: Triggers events based on schedules or system events
- **Lambda**: Processes events and formats notification content
- **SNS**: Delivers notifications via email and SMS

## Use Cases
- System alerts and monitoring notifications
- Scheduled reminders and reports
- User account notifications (password resets, confirmations)
- Event-driven workflows (order confirmations, status updates)

## Cost
- **EventBridge**: $1 per million events (essentially free for most use cases)
- **Lambda**: Free tier covers 1M requests/month + 400K GB-seconds (negligible cost)
- **SNS (Email)**: $2 per 100,000 notifications (~$0.20 for 10K emails)
- **SNS (Text)**: $0.00645 per SMS in US, varies internationally (primary cost driver)

**Note:** SMS costs can scale significantly - approximately 300x more expensive than email notifications.
                        `}
                    </ReactMarkdown>
                </div>

                <div className="code-title-container" data-section="instantiation-code">
                    <ReactMarkdown>
                        {`# app.ts`}
                    </ReactMarkdown>
                </div>

                 <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
#!/usr/bin/env node
import * as cdk from 'aws-cdk-lib/core';
import { EventDrivenNotificationStack } from '../lib/event_driven_notification_stack';

const app = new cdk.App();

new EventDrivenNotificationStack(app, "DevNotifications", { 
  scheduleExpression: "rate(5 minutes)",
  enableEmail: true,
  emailAddress: "example@gmail.com",
  enableSms: false,
  env: {
    account: process.env.CDK_DEFAULT_ACCOUNT,
    region: process.env.CDK_DEFAULT_REGION
  }
})
`}
                    </SyntaxHighlighter>
                </div>
                    
                <div className="code-title-container" data-section="stack-code">
                    <ReactMarkdown>
                        {`# event-driven-notification-stack.ts`}
                    </ReactMarkdown>
                </div>

                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
                        {`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as sns from "aws-cdk-lib/aws-sns";
import * as subscriptions from "aws-cdk-lib/aws-sns-subscriptions"
import * as lambda from "aws-cdk-lib/aws-lambda";
import * as events from "aws-cdk-lib/aws-events";
import * as targets from "aws-cdk-lib/aws-events-targets"

interface EventDrivenNotificationStackProps extends cdk.StackProps {
scheduleExpression?: string;
emailAddress?: string;
phoneNumber?: string;
enableSms?: boolean;
enableEmail?: boolean;
eventPattern?: any;
}

export class EventDrivenNotificationStack extends cdk.Stack {
constructor(scope: Construct, id: string, props?: EventDrivenNotificationStackProps){
    super(scope, id, props)

    const notificationTopic = new sns.Topic(this, "NotificationTopic", {
    displayName: "Event Notifications",
    })

    if (props?.enableEmail && props?.emailAddress) {
    notificationTopic.addSubscription(new subscriptions.EmailSubscription(props.emailAddress))
    }

    if (props?.enableSms && props?.phoneNumber) {
    notificationTopic.addSubscription(new subscriptions.SmsSubscription(props.phoneNumber))
    }

    const notificationFunction = new lambda.Function(this, "NotificationFunction", {
    runtime: lambda.Runtime.NODEJS_18_X,
    handler: "index.handler",
    code: lambda.Code.fromAsset("lambdas/notification_handler"),
    environment: {
        TOPIC_ARN: notificationTopic.topicArn,
    }
    })

    notificationTopic.grantPublish(notificationFunction);

    const rule = new events.Rule(this, "NotificationRule", {
    description: "Trigger notifications based on schedule or events",
    schedule: props?.scheduleExpression ? events.Schedule.expression(props.scheduleExpression) : undefined,
    eventPattern: props?.eventPattern,
    })

    rule.addTarget(new targets.LambdaFunction(notificationFunction));

    new cdk.CfnOutput(this, "TopicArn", {
    value: notificationTopic.topicArn,
    description: "SMS topic arn for notifications",
    })

    new cdk.CfnOutput(this, "FunctionName", {
    value: notificationFunction.functionName,
    description: "Function for notifications"
    })
    
    new cdk.CfnOutput(this, "RuleName", {
    value: rule.ruleName,
    description: "EventBridge Rule Name"
    })

}
}
                        `}
                    </SyntaxHighlighter>
                </div>
                <div className="code-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Implementation Details`}
                    </ReactMarkdown>
                </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Import Statements & Stack Props`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as sns from "aws-cdk-lib/aws-sns";
import * as subscriptions from "aws-cdk-lib/aws-sns-subscriptions"
import * as lambda from "aws-cdk-lib/aws-lambda";
import * as events from "aws-cdk-lib/aws-events";
import * as targets from "aws-cdk-lib/aws-events-targets"

interface EventDrivenNotificationStackProps extends cdk.StackProps {
  scheduleExpression?: string;
  emailAddress?: string;
  phoneNumber?: string;
  enableSms?: boolean;
  enableEmail?: boolean;
  eventPattern?: any;
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Imports
- **aws-cdk-lib/core** - Core CDK constructs for defining stacks, apps, and basic infrastructure components
- **constructs** - Base Construct class that all CDK constructs extend from
- **aws-sns** - Simple Notification Service constructs for pub/sub messaging and multi-protocol notifications
- **aws-sns-subscriptions** - Subscription constructs for connecting endpoints (email, SMS, Lambda) to SNS topics
- **aws-lambda** - Lambda function constructs for serverless compute and event processing
- **aws-events** - EventBridge constructs for creating event-driven architectures with rules and patterns
- **aws-events-targets** - Target constructs that define where EventBridge events are sent (Lambda, SNS, SQS, etc.)
## Props
- **scheduleExpression** -  cron or rate expression for scheduled events (e.g., "rate(5 minutes)", "cron(0 12 * * ? *)")
- **emailAddress** -  email address to receive notifications via SNS
- **phoneNumber** -  phone number to receive SMS notifications (must be in E.164 format like +1234567890)
- **enableSms** -  flag to enable/disable SMS notifications
- **enableEmail** -  flag to enable/disable email notifications
- **eventPattern** -  EventBridge event pattern object to filter which events trigger notifications (e.g., match specific sources, detail types)
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Constructor & SNS`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
export class EventDrivenNotificationStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props?: EventDrivenNotificationStackProps){
    super(scope, id, props)

    const notificationTopic = new sns.Topic(this, "NotificationTopic", {
      displayName: "Event Notifications",
    })

    if (props?.enableEmail && props?.emailAddress) {
      notificationTopic.addSubscription(new subscriptions.EmailSubscription(props.emailAddress))
    }

    if (props?.enableSms && props?.phoneNumber) {
      notificationTopic.addSubscription(new subscriptions.SmsSubscription(props.phoneNumber))
    }
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Constructor
- Takes a scope (parent construct), id (unique identifier), and optional props to configure the stack
- Calls super() to initialize the parent cdk.Stack class with the provided parameters
## SNS
- Creates an SNS topic as the central hub for publishing and distributing notifications
- **displayName** - Sets a human-readable name "Event Notifications" shown in AWS Console and notification messages
- Conditionally adds an email subscription only if both enableEmail is true AND emailAddress is provided
- Conditionally adds an SMS subscription only if both enableSms is true AND phoneNumber is provided
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Lambda Function`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    const notificationFunction = new lambda.Function(this, "NotificationFunction", {
      runtime: lambda.Runtime.NODEJS_18_X,
      handler: "index.handler",
      code: lambda.Code.fromAsset("lambdas/notification_handler"),
      environment: {
        TOPIC_ARN: notificationTopic.topicArn,
      }
    })

    notificationTopic.grantPublish(notificationFunction);
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Function
- Creates a Lambda function to process events and publish notifications to the SNS topic
- **runtime** - Uses Node.js 18.x runtime (can be changed to other supported runtimes)
- **handler** - Entry point expects "index.handler" in the function code
- **code** - Loads function code from the "lambdas/notification_handler" directory (must be manually created and implemented)
- **environment.TOPIC_ARN** - Passes the SNS topic ARN as an environment variable so the function knows where to publish messages
- **grantPublish(notificationFunction)** - Grants the Lambda function IAM permissions to publish messages to the SNS topic
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Eventbridge Rule `}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    const rule = new events.Rule(this, "NotificationRule", {
      description: "Trigger notifications based on schedule or events",
      schedule: props?.scheduleExpression ? events.Schedule.expression(props.scheduleExpression) : undefined,
      eventPattern: props?.eventPattern,
    })

    rule.addTarget(new targets.LambdaFunction(notificationFunction));

`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Rule
- Creates an event rule that triggers notifications based on either scheduled intervals or custom event patterns
- **description** - Documents the rule's purpose for clarity in the AWS Console
- **schedule** -  triggers on a schedule using cron or rate expressions (e.g., "rate(5 minutes)", "cron(0 9 * * ? *)") if provided in props
- **eventPattern** -  filters and matches specific events from AWS services or custom applications based on the pattern provided in props
- **addTarget(LambdaFunction)** - Configures the rule to invoke the notification Lambda function when triggered
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Outputs`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    new cdk.CfnOutput(this, "TopicArn", {
      value: notificationTopic.topicArn,
      description: "SMS topic arn for notifications",
    })

    new cdk.CfnOutput(this, "FunctionName", {
      value: notificationFunction.functionName,
      description: "Function for notifications"
    })
    
    new cdk.CfnOutput(this, "RuleName", {
      value: rule.ruleName,
      description: "EventBridge Rule Name"
    })

  
  }
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Outputs
- These outputs display important values in the terminal after deployment and can be referenced by other stacks or automation tools
`}
                    </ReactMarkdown>
                    </div>
            </main>
        </div>
    )
}

export default EventDrivenNotification;